<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useDashboardStore } from "@/modules/dashboards/store";

const visible = defineModel<boolean>();

const dashboardState = useDashboardStore().dashboardState;

const dashboardModes = [
	{
		value: "web",
		label: "Web",
		icon: "fa-regular fa-desktop",
		description:
			"An interactive dashboard designed for viewing in the browser. Dashboards in web mode cannot be exported or converted to PDF.",
	},
	{
		value: "print",
		label: "Print",
		icon: "fa-regular fa-print",
		description:
			"The print view is optimized to reflect how the PDF (a4) will look. Because of this, it is less suitable for interactive use in the browser. Only dashboards in print mode can be downloaded or exported as a PDF.",
	},
] as const;

const dashboardTimeConfigurations = [
	{
		value: "subjectDate",
		label: "Dashboard subject dates",
		description:
			"Define From and To dates that override the subject dates from the original card configuration.",
		use_single_subject_date: true,
		use_time_context: false,
	},
	{
		value: "cardDates",
		label: "Card configuration dates",
		description:
			"Use the From and To date as defined in the original card configuration.",
		use_single_subject_date: false,
		use_time_context: false,
	},
	{
		value: "timeContext",
		label: "Card Configuration  + Time Context",
		description:
			"Define what ‘now’ means when resolving dynamic dates for the original card configuration.",
		use_single_subject_date: false,
		use_time_context: true,
	},
] as const;

const yesNoOptions = [
	{ value: true, label: "Yes" },
	{ value: false, label: "No" },
] as const;

const dashboardSnapshotConfigurations = [
	{
		value: true,
		label: "Dashboard snapshot date",
		description:
			"Define a date to override the snapshot date from the original card configuration.",
	},
	{
		value: false,
		label: "Card configuration date",
		description:
			"Use the snapshot date as defined in the original card configuration.",
	},
] as const;

// Local draft — only written back to dashboardState when the user clicks Apply.
const draftSettings = reactive({
	mode: dashboardState.dashboard.mode,
	legendWidth: dashboardState.dashboard.legend_width,
	useSingleMeasureDate: dashboardState.dashboard.use_single_measure_date,
	useTimeContextForSnapshotDate:
		dashboardState.dashboard.use_time_context_for_snapshot_date,
});

const selectedDashboardMode = computed(() =>
	dashboardModes.find((mode) => mode.value === draftSettings.mode)
);

type TimeConfiguration = (typeof dashboardTimeConfigurations)[number]["value"];

function determineActiveTimeConfiguration(): TimeConfiguration {
	if (dashboardState.dashboard.use_time_context) {
		return "timeContext";
	}
	if (!dashboardState.dashboard.use_single_subject_date) {
		return "cardDates";
	}
	return "subjectDate";
}

const timeConfiguration = ref<TimeConfiguration>(
	determineActiveTimeConfiguration()
);

const selectedTimeConfiguration = computed(() =>
	dashboardTimeConfigurations.find(
		(configuration) => configuration.value === timeConfiguration.value
	)
);

const selectedDashboardSnapshotConfiguration = computed(() =>
	dashboardSnapshotConfigurations.find(
		(configuration) =>
			configuration.value === draftSettings.useSingleMeasureDate
	)
);

const customLegendWidthEnabled = computed({
	get() {
		return draftSettings.legendWidth !== null;
	},

	set(v) {
		draftSettings.legendWidth = v ? 200 : null;
	},
});

function closeDialog() {
	visible.value = false;
}

watch(
	() => draftSettings.useTimeContextForSnapshotDate,
	(v) => {
		if (v) {
			draftSettings.useSingleMeasureDate = false;
		}
	}
);

function applySettings() {
	dashboardState.dashboard.mode = draftSettings.mode;
	dashboardState.dashboard.legend_width = draftSettings.legendWidth;
	dashboardState.dashboard.use_single_measure_date =
		draftSettings.useSingleMeasureDate;

	if (selectedTimeConfiguration.value) {
		dashboardState.dashboard.use_single_subject_date =
			selectedTimeConfiguration.value.use_single_subject_date;
		dashboardState.dashboard.use_time_context =
			selectedTimeConfiguration.value.use_time_context;
		dashboardState.dashboard.use_time_context_for_snapshot_date =
			draftSettings.useTimeContextForSnapshotDate;
	}

	visible.value = false;
}
</script>

<template>
	<v-dialog
		v-model="visible"
		scrim="#000000"
		overlay-color="black"
		width="1000"
	>
		<v-card>
			<v-toolbar style="border-radius: 0 !important" color="lightprimary" flat>
				<template v-slot:prepend>
					<v-icon start class="ml-3 mr-5">fa: fa-light fa-cog</v-icon>
					<v-toolbar-title class="pt-1">Dashboard Settings</v-toolbar-title>
				</template>
				<v-spacer></v-spacer>

				<v-btn icon @click="visible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-divider />

			<div class="ma-3">
				<div class="field-group">
					<div class="text-caption pb-2">Dashboard Mode</div>
					<v-btn-toggle
						v-model="draftSettings.mode"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
					>
						<v-btn
							v-tooltip="mode.description"
							v-for="mode in dashboardModes"
							:key="mode.value"
							:value="mode.value"
							:prepend-icon="mode.icon"
							append-icon="fa: fa-light fa-circle-info"
						>
							{{ mode.label }}
						</v-btn>
					</v-btn-toggle>
				</div>

				<v-divider class="mt-5 mb-3" />

				<div class="field-group">
					<div class="text-caption pb-2">Subject Dates</div>
					<v-btn-toggle
						v-model="timeConfiguration"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
					>
						<v-btn
							v-tooltip="configuration.description"
							v-for="configuration in dashboardTimeConfigurations"
							:key="configuration.value"
							:value="configuration.value"
							append-icon="fa: fa-light fa-circle-info"
						>
							{{ configuration.label }}
						</v-btn>
					</v-btn-toggle>

					<v-expand-transition>
						<div class="mt-3" v-if="timeConfiguration === 'timeContext'">
							<div class="text-caption pb-2">
								Apply the user defined Time Context also to Snapshot Dates
							</div>
							<v-btn-toggle
								v-model="draftSettings.useTimeContextForSnapshotDate"
								mandatory
								density="compact"
								variant="outlined"
								color="primary"
							>
								<v-btn
									v-for="option in yesNoOptions"
									:key="String(option.value)"
									:value="option.value"
								>
									{{ option.label }}
								</v-btn>
							</v-btn-toggle>
						</div>
					</v-expand-transition>
				</div>

				<v-divider class="mt-5 mb-3" />

				<div class="field-group">
					<div class="text-caption pb-2">
						Snapshot Dates
						<span
							v-if="
								timeConfiguration === 'timeContext' &&
								draftSettings.useTimeContextForSnapshotDate
							"
							class="ml-2"
							><i
								>[ disabled because of subject date, time-context settings ]</i
							></span
						>
					</div>
					<v-btn-toggle
						:disabled="
							timeConfiguration === 'timeContext' &&
							draftSettings.useTimeContextForSnapshotDate
						"
						v-model="draftSettings.useSingleMeasureDate"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
					>
						<v-btn
							v-for="configuration in dashboardSnapshotConfigurations"
							:key="String(configuration.value)"
							:value="configuration.value"
							v-tooltip="configuration.description"
							append-icon="fa: fa-light fa-circle-info"
						>
							{{ configuration.label }}
						</v-btn>
					</v-btn-toggle>
				</div>

				<v-divider class="mt-5 mb-3" />

				<div class="field-group">
					<div class="text-caption pb-2">Use custom width for all legends</div>
					<v-btn-toggle
						v-model="customLegendWidthEnabled"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
					>
						<v-btn
							v-for="option in yesNoOptions"
							:key="String(option.value)"
							:value="option.value"
						>
							{{ option.label }}
						</v-btn>
					</v-btn-toggle>

					<v-expand-transition>
						<div v-if="customLegendWidthEnabled">
							<v-number-input
								v-model="draftSettings.legendWidth"
								:max="1000"
								:min="50"
								:step="10"
								class="mt-3"
								density="compact"
								control-variant="stacked"
								inset
								hide-details
							/>
						</div>
					</v-expand-transition>
				</div>
			</div>

			<v-divider />
			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" color="error" @click="closeDialog"> Close </v-btn>
				<v-btn variant="flat" color="primary" @click="applySettings">
					Apply
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>
